import {
  Button, Fieldset, Group, Modal, MultiSelect, NumberInput, Select, SimpleGrid, Stack, Switch,
  TagsInput, Text, TextInput
} from "@mantine/core";
import { ArrowRightIcon, BellIcon, ListBulletsIcon, WarningCircleIcon } from "@phosphor-icons/react";
import { ConfirmNote, DisabledReason, HelpButtonGroup, HelpIcon, nextStepToast, useHelpContext } from "@dehub/theme";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import useSWR from "swr";
import { TenantReturnsSettingsApi } from "../../apis";
import type { ITenantReturnsSettings } from "../../apis/tenant-returns-settings";
import { PageLayout } from "../../components/layout";
import { usePermission } from "../../components/permission";
import { DetailCard } from "../../components/return/detail-card";
import { DeniedCard } from "../../components/ui";
import { message } from "../../utils/message";
import { ReturnModuleTabs } from "../../components/return/module-tabs";
import { REASON_OPTION_VALUES } from "../../components/return/list-filters";
import { reasonLabel } from "../../components/return/atoms";

const CHANNEL_VALUES = ["IN_APP", "EMAIL", "SLACK"];
const REFUND_METHOD_VALUES = ["BANK_TRANSFER", "INVOICE_DEDUCTION", "CASH", "CREDIT_BALANCE"];
const TRANSIT_FALLBACK_VALUES = ["SELLER_ABSORB", "BUYER_ABSORB", "INSURANCE_PAYOUT", "CARRIER_LIABILITY"];

// Documented defaults for the General-policy + Refund-SLA sections (Sections
// 22/23). "Reset to defaults" applies these to the form, then saves.
const POLICY_DEFAULTS: Partial<ITenantReturnsSettings> = {
  defaultRefundMethod                 : "BANK_TRANSFER",
  autoApproveThresholdAmount          : null,
  couponValidityDays                  : 90,
  disputeCountMax                     : 3,
  replacementShipFirstEnabled         : false,
  invoiceDeductionFallbackEnabled     : true,
  invoiceDeductionFallbackChannel     : "BANK_TRANSFER",
  staleRequestedAutoCancelEnabled     : true,
  staleRequestedAutoCancelDays        : 14,
  refundSlaBankTransferMinDays        : 3,
  refundSlaBankTransferDays           : 5,
  refundSlaInvoiceDeductionDays       : 0,
  refundSlaCashDays                   : 0,
  financeConfirmationSlaDays          : 3,
  financeConfirmationEscalationEnabled: true,
  pickupSlaSellerDays                 : 14,
  pickupSlaBuyerDays                  : 14,
  pickupSlaBreachEventEnabled         : true,
  warehouseReceiptSlaDays             : 7,
  warehouseReceiptBreachEventEnabled  : true,
  transitExceptionSlaDays             : 14,
  transitExceptionAutoResolveFallback : "SELLER_ABSORB",
  escalationCron                      : "0 */1 * * *"
};

// O178 — label of each field «Анхдагч руу буцаах» touches, so the confirm modal can list them.
const POLICY_FIELD_LABEL: Record<string, string> = {
  defaultRefundMethod                 : "settings.policy.defaultRefundMethod",
  autoApproveThresholdAmount          : "settings.policy.autoApproveThreshold",
  couponValidityDays                  : "settings.policy.couponValidity",
  disputeCountMax                     : "settings.policy.disputeMax",
  replacementShipFirstEnabled         : "settings.policy.replacementShipFirst",
  invoiceDeductionFallbackEnabled     : "settings.policy.invoiceFallbackEnable",
  invoiceDeductionFallbackChannel     : "settings.policy.invoiceFallbackMethod",
  staleRequestedAutoCancelEnabled     : "settings.policy.staleAutoCancelEnable",
  staleRequestedAutoCancelDays        : "settings.policy.staleAutoCancel",
  refundSlaBankTransferMinDays        : "settings.refundSla.bankMin",
  refundSlaBankTransferDays           : "settings.refundSla.bankMax",
  refundSlaInvoiceDeductionDays       : "settings.refundSla.invoiceDeduction",
  refundSlaCashDays                   : "settings.refundSla.cash",
  financeConfirmationSlaDays          : "settings.timePolicy.financeSla",
  financeConfirmationEscalationEnabled: "settings.timePolicy.financeEscalation",
  pickupSlaSellerDays                 : "settings.timePolicy.pickupSeller",
  pickupSlaBuyerDays                  : "settings.timePolicy.pickupBuyer",
  pickupSlaBreachEventEnabled         : "settings.timePolicy.pickupBreach",
  warehouseReceiptSlaDays             : "settings.timePolicy.warehouseReceipt",
  warehouseReceiptBreachEventEnabled  : "settings.timePolicy.warehouseBreach",
  transitExceptionSlaDays             : "settings.timePolicy.transitException",
  transitExceptionAutoResolveFallback : "settings.timePolicy.transitFallback",
  escalationCron                      : "settings.timePolicy.escalationCron"
};

// O167 — only the fields the user changed go to PUT (the BE also drops read-only keys, but a minimal body is the contract).
function changedFields (base: ITenantReturnsSettings, next: Partial<ITenantReturnsSettings>): Partial<ITenantReturnsSettings> {
  const patch: Record<string, unknown> = {};
  for (const key of Object.keys(next) as (keyof ITenantReturnsSettings)[]) {
    if (JSON.stringify(next[key]) !== JSON.stringify(base[key])) patch[key] = next[key];
  }
  return patch as Partial<ITenantReturnsSettings>;
}

/** @dehub/help key namespace of this screen (spec 12 §0). */
const HELP = "order.returns-settings";

/** Field label with the inline «?» (`order.returns-settings.field.{id}`). */
const helpLabel = (label: string, id: string) => <>{label}<HelpIcon helpKey={`${HELP}.field.${id}`} /></>;

// Section card: title 17px + 14px description (canvas Ord_ReturnSettings). `locked` disables every control inside (no BIZ_GOV_MNG).
function Section ({ title, desc, locked, helpId, children }: { title: string; desc?: string; locked?: boolean; helpId?: string; children: ReactNode }){
  return (
    <DetailCard title={title} helpKey={helpId ? `${HELP}.section.${helpId}` : undefined}>
      <Fieldset disabled={locked} variant="unstyled" p={0} m={0}>
        <Stack gap="md">
          {desc ? <Text size="sm" c="var(--nw-text-2)">{desc}</Text> : null}
          {children}
        </Stack>
      </Fieldset>
    </DetailCard>
  );
}

// Link card to a sub-page (templates, failed operations).
function LinkCard ({ icon, title, desc, to, label, helpId }: { icon: ReactNode; title: string; desc: string; to: string; label: string; helpId: string }){
  return (
    <DetailCard
      title={<Group gap="sm" wrap="nowrap">{icon}<Text span inherit>{title}</Text></Group>}
      helpKey={`${HELP}.section.${helpId}`}
      extra={<Button component={Link} to={to} variant="default" rightSection={<ArrowRightIcon size={16} />}>{label}</Button>}
    >
      <Text size="sm" c="var(--nw-text-2)">{desc}</Text>
    </DetailCard>
  );
}

export function ReturnsSettingsPage (){
  const { t } = useTranslation("returns");
  const BREADCRUMB = [
    { to: "/", label: t("breadcrumb.home") },
    { to: "/returns", label: t("breadcrumb.returns") },
    { label: t("breadcrumb.settings") }
  ];
  const channelOptions = CHANNEL_VALUES.map((v) => ({ value: v, label: t(`settings.channel.${v}`) }));
  const refundMethodOptions = REFUND_METHOD_VALUES.map((v) => ({ value: v, label: t(`settings.refundMethod.${v}`) }));
  const transitFallbackOptions = TRANSIT_FALLBACK_VALUES.map((v) => ({ value: v, label: t(`settings.transitFallback.${v}`) }));
  const { data, mutate, isLoading } = useSWR(
    ["tenant-returns-settings"],
    () => TenantReturnsSettingsApi.get(),
    { revalidateOnFocus: false }
  );

  const [draft, setDraft] = useState<Partial<ITenantReturnsSettings>>({});
  const [busy, setBusy] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);

  // GET = RETURN_VIEW or BIZ_GOV_VIEW, PUT = BIZ_GOV_MNG (BE tenant-returns-settings/*).
  const returnView = usePermission("RETURN_VIEW");
  const govView = usePermission("BIZ_GOV_VIEW");
  const govMng = usePermission("BIZ_GOV_MNG");
  const canView = returnView.hasAccess || govView.hasAccess;
  const canEdit = govMng.hasAccess;

  useEffect(() => {
    if (data) setDraft(data);
  }, [data]);

  const view = useMemo(() => ({ ...data, ...draft }) as ITenantReturnsSettings, [data, draft]);
  const changed = useMemo(() => (data ? changedFields(data, draft) : {}), [data, draft]);
  const changedCount = Object.keys(changed).length;

  const set = <K extends keyof ITenantReturnsSettings>(key: K, value: ITenantReturnsSettings[K]) => {
    setDraft((d) => ({ ...d, [key]: value }));
  };

  const persist = async (patch: Partial<ITenantReturnsSettings>) => {
    if (!data || Object.keys(patch).length === 0) return;
    try {
      setBusy(true);
      await TenantReturnsSettingsApi.update(patch);
      nextStepToast(`${HELP}.toast.saved`, { fallback: { title: t("settings.saved") } });
      await mutate();
    } catch (err) {
      message.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const save = () => persist(changed);

  // O178 — «Анхдагч руу буцаах» touches only the General-policy, Refund-SLA and time-policy fields; the modal lists those that differ.
  const resetFields = useMemo(
    () => (data ? Object.keys(changedFields(view, POLICY_DEFAULTS)) : []),
    [data, view]
  );
  const confirmReset = async () => {
    if (!data) return;
    const merged = { ...draft, ...POLICY_DEFAULTS };
    setDraft(merged);
    setResetOpen(false);
    await persist(changedFields(data, merged));
  };

  const saveReason = !canEdit
    ? govMng.reason
    : changedCount === 0 ? t("settings.nothingToSave") : null;
  const saveCode = !canEdit ? "PERM" : changedCount === 0 ? "NO_CHANGE" : null;

  // @dehub/help: read-only access and unsaved changes drive the drawer's «Одоо» cards.
  const { set: setHelpContext } = useHelpContext();
  useEffect(() => {
    setHelpContext({ vars: { readOnly: canEdit ? 0 : 1, changedCount } });
  }, [canEdit, changedCount, setHelpContext]);

  if (!canView) {
    return (
      <PageLayout title={t("settings.pageTitle")} breadcrumb={BREADCRUMB}>
        <DeniedCard permission="RETURN_VIEW / BIZ_GOV_VIEW" />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={t("settings.pageTitle")}
      subTitle={t("settings.pageSubtitle")}
      breadcrumb={BREADCRUMB}
      loading={isLoading && !data}
      extra={[
        // @dehub/help: attached «?» (R2) + reason popover while there is nothing to save or no permission (R4).
        <DisabledReason key="save" helpKey={`${HELP}.reason.save`} reason={saveCode} when={!!saveReason} position="bottom-end">
          <HelpButtonGroup helpKey={`${HELP}.action.save`}>
            <Button data-help-id="returns-settings-save" onClick={save} loading={busy} disabled={!data}>{t("settings.save")}</Button>
          </HelpButtonGroup>
        </DisabledReason>
      ]}
    >
      {!data ? null : (
        <Stack gap="md">
          <ReturnModuleTabs active="settings" />
          {!canEdit ? (
            <Text size="sm" c="var(--nw-text-2)" role="status">{govMng.reason} {t("settings.readOnlyNote")}</Text>
          ) : null}
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md" verticalSpacing="md" style={{ alignItems: "start" }}>
            <Stack gap="md">
            <Section helpId="trust" title={t("settings.trust.title")} desc={t("settings.trust.desc")} locked={!canEdit}>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                <NumberInput
                  label={helpLabel(t("settings.trust.windowDays"), "trust-window")}
                  description={t("settings.trust.windowDaysDesc")}
                  value={view.trustScoreWindowDays}
                  onChange={(v) => set("trustScoreWindowDays", typeof v === "number" ? v : 90)}
                  min={7} max={365}
                />
                <NumberInput
                  label={helpLabel(t("settings.trust.excellentThreshold"), "trust-thresholds")}
                  description={t("settings.trust.excellentThresholdDesc")}
                  value={view.trustExcellentThreshold}
                  onChange={(v) => set("trustExcellentThreshold", typeof v === "number" ? v : 90)}
                  min={0} max={100} decimalScale={2}
                />
                <NumberInput
                  label={t("settings.trust.goodThreshold")}
                  description={t("settings.trust.goodThresholdDesc")}
                  value={view.trustGoodThreshold}
                  onChange={(v) => set("trustGoodThreshold", typeof v === "number" ? v : 75)}
                  min={0} max={100} decimalScale={2}
                />
                <NumberInput
                  label={t("settings.trust.moderateThreshold")}
                  description={t("settings.trust.moderateThresholdDesc")}
                  value={view.trustModerateThreshold}
                  onChange={(v) => set("trustModerateThreshold", typeof v === "number" ? v : 50)}
                  min={0} max={100} decimalScale={2}
                />
                <NumberInput
                  label={t("settings.trust.atRiskThreshold")}
                  description={t("settings.trust.atRiskThresholdDesc")}
                  value={view.trustAtRiskThreshold}
                  onChange={(v) => set("trustAtRiskThreshold", typeof v === "number" ? v : 25)}
                  min={0} max={100} decimalScale={2}
                />
                <NumberInput
                  label={helpLabel(t("settings.trust.highValueThreshold"), "high-value")}
                  description={t("settings.trust.highValueThresholdDesc")}
                  value={view.highValueThresholdAmount}
                  onChange={(v) => set("highValueThresholdAmount", typeof v === "number" ? v : 1_000_000)}
                  min={0} thousandSeparator
                />
              </SimpleGrid>
              <Text size="sm" c="var(--nw-text-2)">
                {t("settings.trust.orderHint")}
              </Text>
            </Section>
            <Section helpId="prevention" title={t("settings.prevention.title")} desc={t("settings.prevention.desc")} locked={!canEdit}>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                <NumberInput
                  label={helpLabel(t("settings.prevention.rateThreshold"), "rate-threshold")}
                  description={t("settings.prevention.rateThresholdDesc")}
                  value={view.preventionReturnRateThresholdPercent}
                  onChange={(v) => set("preventionReturnRateThresholdPercent", typeof v === "number" ? v : 10)}
                  min={0} max={100} decimalScale={2}
                />
                <NumberInput
                  label={helpLabel(t("settings.prevention.minSold"), "min-sold")}
                  description={t("settings.prevention.minSoldDesc")}
                  value={view.preventionMinSoldQuantity}
                  onChange={(v) => set("preventionMinSoldQuantity", typeof v === "number" ? v : 50)}
                  min={1} max={10_000}
                />
                <NumberInput
                  label={t("settings.prevention.windowDays")}
                  description={t("settings.prevention.windowDaysDesc")}
                  value={view.preventionWindowDays}
                  onChange={(v) => set("preventionWindowDays", typeof v === "number" ? v : 30)}
                  min={7} max={365}
                />
                <NumberInput
                  label={helpLabel(t("settings.prevention.spikeMultiplier"), "spike")}
                  description={t("settings.prevention.spikeMultiplierDesc")}
                  value={view.preventionSpikeMultiplier}
                  onChange={(v) => set("preventionSpikeMultiplier", typeof v === "number" ? v : 2)}
                  min={1} max={10} decimalScale={2}
                />
              </SimpleGrid>
            </Section>
            <Section helpId="alert-channel" title={t("settings.alertChannel.title")} desc={t("settings.alertChannel.desc")} locked={!canEdit}>
              <Stack gap="md">
                <MultiSelect
                  label={t("settings.alertChannel.channels")}
                  description={t("settings.alertChannel.channelsDesc")}
                  data={channelOptions}
                  value={view.alertDefaultChannels ?? []}
                  onChange={(v) => set("alertDefaultChannels", v)}
                />
                <TagsInput
                  label={t("settings.alertChannel.emailRecipients")}
                  value={view.alertEmailRecipients ?? []}
                  onChange={(v) => set("alertEmailRecipients", v)}
                  placeholder="alerts@example.com"
                />
                <TextInput
                  label={t("settings.alertChannel.slackWebhook")}
                  value={view.alertSlackWebhook ?? ""}
                  onChange={(e) => set("alertSlackWebhook", e.currentTarget.value)}
                  placeholder="https://hooks.slack.com/services/..."
                />
              </Stack>
            </Section>
            <Section helpId="time-policy" title={t("settings.timePolicy.title")} desc={t("settings.timePolicy.desc")} locked={!canEdit}>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                <NumberInput
                  label={t("settings.timePolicy.financeSla")}
                  value={view.financeConfirmationSlaDays}
                  onChange={(v) => set("financeConfirmationSlaDays", typeof v === "number" ? v : 3)}
                  min={0} max={365}
                />
                <NumberInput
                  label={t("settings.timePolicy.pickupSeller")}
                  value={view.pickupSlaSellerDays}
                  onChange={(v) => set("pickupSlaSellerDays", typeof v === "number" ? v : 14)}
                  min={0} max={365}
                />
                <NumberInput
                  label={t("settings.timePolicy.pickupBuyer")}
                  value={view.pickupSlaBuyerDays}
                  onChange={(v) => set("pickupSlaBuyerDays", typeof v === "number" ? v : 14)}
                  min={0} max={365}
                />
                <NumberInput
                  label={t("settings.timePolicy.warehouseReceipt")}
                  value={view.warehouseReceiptSlaDays}
                  onChange={(v) => set("warehouseReceiptSlaDays", typeof v === "number" ? v : 7)}
                  min={0} max={365}
                />
                <NumberInput
                  label={t("settings.timePolicy.transitException")}
                  value={view.transitExceptionSlaDays}
                  onChange={(v) => set("transitExceptionSlaDays", typeof v === "number" ? v : 14)}
                  min={0} max={365}
                />
                <Select
                  label={helpLabel(t("settings.timePolicy.transitFallback"), "transit-fallback")}
                  description={t("settings.timePolicy.transitFallbackDesc")}
                  data={transitFallbackOptions}
                  value={view.transitExceptionAutoResolveFallback}
                  onChange={(v) => set("transitExceptionAutoResolveFallback", v ?? "SELLER_ABSORB")}
                />
                <TextInput
                  label={helpLabel(t("settings.timePolicy.escalationCron"), "escalation-cron")}
                  description={t("settings.timePolicy.escalationCronDesc")}
                  value={view.escalationCron ?? ""}
                  onChange={(e) => set("escalationCron", e.currentTarget.value)}
                  placeholder="0 */1 * * *"
                />
              </SimpleGrid>
              <Stack gap="xs" mt="md">
                <Switch
                  label={t("settings.timePolicy.financeEscalation")}
                  checked={view.financeConfirmationEscalationEnabled}
                  onChange={(e) => set("financeConfirmationEscalationEnabled", e.currentTarget.checked)}
                />
                <Switch
                  label={t("settings.timePolicy.pickupBreach")}
                  checked={view.pickupSlaBreachEventEnabled}
                  onChange={(e) => set("pickupSlaBreachEventEnabled", e.currentTarget.checked)}
                />
                <Switch
                  label={t("settings.timePolicy.warehouseBreach")}
                  checked={view.warehouseReceiptBreachEventEnabled}
                  onChange={(e) => set("warehouseReceiptBreachEventEnabled", e.currentTarget.checked)}
                />
              </Stack>
            </Section>
            </Stack>
            <Stack gap="md">
            <Section helpId="policy" title={t("settings.policy.title")} desc={t("settings.policy.desc")} locked={!canEdit}>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                <Select
                  label={t("settings.policy.defaultRefundMethod")}
                  description={t("settings.policy.defaultRefundMethodDesc")}
                  data={refundMethodOptions}
                  value={view.defaultRefundMethod}
                  onChange={(v) => set("defaultRefundMethod", v ?? "BANK_TRANSFER")}
                />
                <NumberInput
                  label={helpLabel(t("settings.policy.autoApproveThreshold"), "auto-approve")}
                  description={t("settings.policy.autoApproveThresholdDesc")}
                  value={view.autoApproveThresholdAmount ?? undefined}
                  onChange={(v) => set("autoApproveThresholdAmount", typeof v === "number" ? v : 0)}
                  min={0}
                  thousandSeparator
                  allowDecimal
                  disabled={view.autoApproveThresholdAmount == null}
                />
                <NumberInput
                  label={t("settings.policy.couponValidity")}
                  description={t("settings.policy.couponValidityDesc")}
                  value={view.couponValidityDays}
                  onChange={(v) => set("couponValidityDays", typeof v === "number" ? v : 90)}
                  min={1} max={365}
                />
                <NumberInput
                  label={helpLabel(t("settings.policy.disputeMax"), "dispute-max")}
                  description={t("settings.policy.disputeMaxDesc")}
                  value={view.disputeCountMax}
                  onChange={(v) => set("disputeCountMax", typeof v === "number" ? v : 3)}
                  min={1} max={20}
                />
                <Select
                  label={helpLabel(t("settings.policy.invoiceFallbackMethod"), "invoice-fallback")}
                  description={t("settings.policy.invoiceFallbackMethodDesc")}
                  data={refundMethodOptions}
                  value={view.invoiceDeductionFallbackChannel}
                  onChange={(v) => set("invoiceDeductionFallbackChannel", v ?? "BANK_TRANSFER")}
                  disabled={!view.invoiceDeductionFallbackEnabled}
                />
                <NumberInput
                  label={helpLabel(t("settings.policy.staleAutoCancel"), "stale-cancel")}
                  description={t("settings.policy.staleAutoCancelDesc")}
                  value={view.staleRequestedAutoCancelDays}
                  onChange={(v) => set("staleRequestedAutoCancelDays", typeof v === "number" ? v : 14)}
                  min={1} max={365}
                  disabled={!view.staleRequestedAutoCancelEnabled}
                />
              </SimpleGrid>

              <Stack gap="xs" mt="md">
                <Switch
                  label={t("settings.policy.autoApproveEnable")}
                  description={t("settings.policy.autoApproveEnableDesc")}
                  checked={view.autoApproveThresholdAmount != null}
                  onChange={(e) => set("autoApproveThresholdAmount", e.currentTarget.checked ? (view.autoApproveThresholdAmount ?? 0) : null)}
                />
                <Switch
                  label={helpLabel(t("settings.policy.replacementShipFirst"), "replacement-first")}
                  description={t("settings.policy.replacementShipFirstDesc")}
                  checked={view.replacementShipFirstEnabled}
                  onChange={(e) => set("replacementShipFirstEnabled", e.currentTarget.checked)}
                />
                <Switch
                  label={t("settings.policy.invoiceFallbackEnable")}
                  description={t("settings.policy.invoiceFallbackEnableDesc")}
                  checked={view.invoiceDeductionFallbackEnabled}
                  onChange={(e) => set("invoiceDeductionFallbackEnabled", e.currentTarget.checked)}
                />
                <Switch
                  label={t("settings.policy.staleAutoCancelEnable")}
                  description={t("settings.policy.staleAutoCancelEnableDesc")}
                  checked={view.staleRequestedAutoCancelEnabled}
                  onChange={(e) => set("staleRequestedAutoCancelEnabled", e.currentTarget.checked)}
                />
                <Switch
                  label={t("settings.policy.ebarimtReceipt")}
                  checked={view.ebarimtReturnReceiptEnabled}
                  onChange={(e) => set("ebarimtReturnReceiptEnabled", e.currentTarget.checked)}
                />
              </Stack>
            </Section>
            <Section helpId="refund-sla" title={t("settings.refundSla.title")} desc={t("settings.refundSla.desc")} locked={!canEdit}>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                <NumberInput
                  label={t("settings.refundSla.bankMin")}
                  value={view.refundSlaBankTransferMinDays}
                  onChange={(v) => set("refundSlaBankTransferMinDays", typeof v === "number" ? v : 0)}
                  min={0}
                />
                <NumberInput
                  label={t("settings.refundSla.bankMax")}
                  value={view.refundSlaBankTransferDays}
                  onChange={(v) => set("refundSlaBankTransferDays", typeof v === "number" ? v : 0)}
                  min={0}
                />
                <NumberInput
                  label={t("settings.refundSla.cash")}
                  value={view.refundSlaCashDays}
                  onChange={(v) => set("refundSlaCashDays", typeof v === "number" ? v : 0)}
                  min={0}
                />
              </SimpleGrid>
              <Text size="sm" c="var(--nw-text-2)">
                {t("settings.refundSla.hint", { days: view.refundSlaInvoiceDeductionDays || 30 })}
              </Text>
            </Section>
            <Section helpId="retention" title={t("settings.retention.title")} locked={!canEdit}>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                <NumberInput
                  label={t("settings.retention.hot")}
                  value={view.retentionHotDays}
                  onChange={(v) => set("retentionHotDays", typeof v === "number" ? v : 0)}
                  min={0}
                />
                <NumberInput
                  label={t("settings.retention.archiveAfter")}
                  value={view.retentionArchiveAfterDays}
                  onChange={(v) => set("retentionArchiveAfterDays", typeof v === "number" ? v : 0)}
                  min={0}
                />
                <NumberInput
                  label={t("settings.retention.erasure")}
                  value={view.retentionErasureSlaDays}
                  onChange={(v) => set("retentionErasureSlaDays", typeof v === "number" ? v : 0)}
                  min={0}
                />
              </SimpleGrid>
            </Section>
            <LinkCard icon={<BellIcon size={20} />} title={t("settings.notifTemplates.title")} desc={t("settings.notifTemplates.desc")} to="/returns/settings/notification-templates" label={t("settings.manageTemplates")} helpId="notif-templates" />
            <LinkCard icon={<ListBulletsIcon size={20} />} title={t("settings.proposeTemplates.title")} desc={t("settings.proposeTemplates.desc")} to="/returns/settings/propose-templates" label={t("settings.manageTemplates")} helpId="propose-templates" />
            <LinkCard icon={<WarningCircleIcon size={20} />} title={t("settings.failedOps.title")} desc={t("settings.failedOps.desc")} to="/returns/settings/failed-operations" label={t("settings.manageOps")} helpId="failed-ops" />
            </Stack>
          </SimpleGrid>
          <Section helpId="sub-reason" title={t("settings.subReason.title")} desc={t("settings.subReason.desc")} locked={!canEdit}>
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
              {REASON_OPTION_VALUES.map((r) => (
                <TagsInput
                  key={r}
                  label={reasonLabel(r)}
                  placeholder={t("settings.subReason.addPlaceholder")}
                  value={view.subReasonTaxonomy?.[r] ?? []}
                  onChange={(tags) =>
                    set("subReasonTaxonomy", {
                      ...(view.subReasonTaxonomy ?? {}),
                      [r]: tags.map((t) => t.slice(0, 40))
                    })
                  }
                />
              ))}
            </SimpleGrid>
          </Section>

          <Group justify="flex-end">
            <DisabledReason helpKey={`${HELP}.reason.reset`} reason="PERM" when={!canEdit} position="bottom-end">
              <HelpButtonGroup helpKey={`${HELP}.action.reset`}>
                <Button variant="default" onClick={() => setResetOpen(true)} loading={busy}>
                  {t("settings.resetDefaults")}
                </Button>
              </HelpButtonGroup>
            </DisabledReason>
          </Group>
        </Stack>
      )}

      {/* O178 — reset confirmation: 440 modal listing the fields it touches */}
      <Modal opened={resetOpen} onClose={() => setResetOpen(false)} title={t("settings.resetModal.title")} size={440}>
        <Stack gap="md">
          <Text size="sm">{t("settings.resetModal.body")}</Text>
          {resetFields.length === 0 ? (
            <Text size="sm" c="var(--nw-text-2)">{t("settings.resetModal.none")}</Text>
          ) : (
            <Stack gap={4}>
              {resetFields.map((f) => (
                <Text key={f} size="sm">{t(POLICY_FIELD_LABEL[f], { defaultValue: "-" })}</Text>
              ))}
            </Stack>
          )}
          <Text size="sm" c="var(--nw-text-2)">{t("settings.resetModal.keeps")}</Text>
          <ConfirmNote helpKey={`${HELP}.confirm.reset`} mt={0} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setResetOpen(false)}>{t("settings.resetModal.cancel")}</Button>
            <Button onClick={confirmReset} loading={busy} disabled={resetFields.length === 0}>{t("settings.resetModal.confirm")}</Button>
          </Group>
        </Stack>
      </Modal>
    </PageLayout>
  );
}
